import type {
  CreateSmartOrderRequest,
  SmartOrder,
  SmartOrderProgress,
  SmartOrderQueryParams,
  SmartOrdersListResponse,
} from "@/types/execution.types";
import {
  API_BASE_URL,
  buildQueryString,
  fetchWithTimeout,
  handleResponse,
} from "./shared";

// ============================================================================
// Smart Orders API (CQG Integration)
// ============================================================================

export const smartOrdersApi = {
  /**
   * List smart orders with optional filters
   */
  async list(params?: SmartOrderQueryParams): Promise<SmartOrdersListResponse> {
    const queryParams = params
      ? {
          symbol: params.symbol,
          smartOrderType: params.smartOrderType,
          status: params.status,
          accountId: params.accountId,
          walletAddress: params.walletAddress,
          limit: params.limit?.toString(),
          offset: params.offset?.toString(),
        }
      : undefined;

    const queryString = buildQueryString(queryParams);
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders${queryString}`
    );
    return handleResponse<SmartOrdersListResponse>(response);
  },

  /**
   * Get a single smart order by ID
   */
  async get(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}`
    );
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Get smart order progress
   */
  async getProgress(id: string): Promise<SmartOrderProgress> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}/progress`
    );
    return handleResponse<SmartOrderProgress>(response);
  },

  /**
   * Create a new smart order
   */
  async create(data: CreateSmartOrderRequest): Promise<SmartOrder> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/cqg/smart-orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Pause a smart order
   */
  async pause(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}/pause`,
      {
        method: "POST",
      }
    );
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Resume a paused smart order
   */
  async resume(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}/resume`,
      {
        method: "POST",
      }
    );
    return handleResponse<SmartOrder>(response);
  },

  /**
   * Cancel a smart order
   */
  async cancel(id: string): Promise<SmartOrder> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/v1/cqg/smart-orders/${id}`,
      {
        method: "DELETE",
      }
    );
    return handleResponse<SmartOrder>(response);
  },
};
